import { WarningFilesCanNotBeAttachedGenericError } from 'owa-locstrings/lib/strings/warningfilescannotbeattachedgenericerror.locstring.json';
import loc from 'owa-localize';
import onAttachmentCreatedAction from './onAttachmentCreated';
import onAttachmentDeletedAction from './onAttachmentDeleted';
import AttachmentWellViewState from '../schema/AttachmentWellViewState';
import { OnAttachmentDeletedCallback } from '../types/AttachmentDeleteQueueItem';
import { CreateAttachmentsProperties } from '../types/CreateAttachmentsProperties';
import clearAttachmentInfoBarMessages from '../utils/AttachmentQueueManager/clearAttachmentInfoBarMessages';
import { logUsage } from 'owa-analytics';
import { assertNever } from 'owa-assert';
import { AttachmentFileList } from 'owa-attachment-file-types';
import { AttachmentFullViewState, AttachmentStateType } from 'owa-attachment-full-data';
import { AttachmentState } from 'owa-attachment-full-data';
import getAttachment from 'owa-attachment-model-store/lib/selectors/getAttachment';
import { ClientAttachmentId, ClientItemId } from 'owa-client-ids';
import { getExtensionWithoutDotFromFileNameForLogging } from 'owa-file';
import { InfoBarHostViewState } from 'owa-info-bar/lib/schema/InfoBarMessageViewState';
import {
    OnAttachmentCreatedCallback,
    OnAttachmentCanceledOrFailedCallback,
} from '../types/AttachmentUploadQueueItem';
import {
    AttachmentQueueManagerInterface,
    getAttachmentQueueManager,
} from '../utils/AttachmentQueueManager/AttachmentQueueManager';
import addInfoBarMessage from 'owa-info-bar/lib/actions/addInfoBarMessage';

const attachmentQueueManagerMap: { [key: string]: AttachmentQueueManagerInterface } = {};
export { attachmentQueueManagerMap };

export default function createAttachmentsViaQueueManager(
    attachmentWellViewState: AttachmentWellViewState,
    parentItemId: ClientItemId,
    files: AttachmentFileList,
    infoBar: InfoBarHostViewState,
    properties: CreateAttachmentsProperties = {
        isInline: false,
        isHiddenInline: false,
        shouldShare: false,
        skipProcessInlineImage: false,
    },
    onFinish?: (attachmentId: ClientAttachmentId) => void,
    onAttachmentCreatedCallback?: OnAttachmentCreatedCallback,
    onAttachmentCanceledOrFailedCallback?: OnAttachmentCanceledOrFailedCallback,
    tryAgainCallback?: () => void
): AttachmentFileList {
    if (!parentItemId) {
        clearAttachmentInfoBarMessages(infoBar);
        addInfoBarMessage(infoBar, 'WarningFilesCanNotBeAttachedGenericError', [
            loc(WarningFilesCanNotBeAttachedGenericError),
        ]);
        return [];
    }
    const manager = getQueueManager(attachmentWellViewState, parentItemId, infoBar, onFinish);

    const { isInline, shouldShare } = properties;
    logAttachmentCreation(files, isInline, shouldShare);
    return manager.enqueueAttachmentCreation(
        files,
        properties,
        onAttachmentCreatedCallback,
        onAttachmentCanceledOrFailedCallback,
        tryAgainCallback
    );
}

export function deleteAttachmentViaQueueManager(
    attachmentWellViewState: AttachmentWellViewState,
    attachmentViewState: AttachmentState,
    parentItemId: ClientItemId,
    infoBar: InfoBarHostViewState,
    onAttachmentDeletedCallback?: OnAttachmentDeletedCallback
) {
    const manager = getQueueManager(attachmentWellViewState, parentItemId, infoBar);
    logAttachmentDeletion(attachmentViewState);
    manager.deleteAttachment(attachmentViewState, onAttachmentDeletedCallback);
}

export function getQueueManager(
    attachmentWellViewState: AttachmentWellViewState,
    parentItemId: ClientItemId,
    infoBar: InfoBarHostViewState,
    onFinish?: (attachmentId: ClientAttachmentId) => void
): AttachmentQueueManagerInterface {
    let queueManager = attachmentQueueManagerMap[parentItemId.Id];

    if (!queueManager) {
        queueManager = getAttachmentQueueManager(
            attachmentWellViewState,
            parentItemId,
            infoBar,
            onAttachmentCreatedAction,
            onAttachmentDeletedAction
        );
        attachmentQueueManagerMap[parentItemId.Id] = queueManager;
        queueManager.onFinishPromise.then(attachmentId => {
            delete attachmentQueueManagerMap[parentItemId.Id];
            if (onFinish) {
                onFinish(attachmentId);
            }
        });
    }

    return queueManager;
}

function logAttachmentCreation(files: AttachmentFileList, isInline: boolean, shouldShare: boolean) {
    for (let i = 0; i < files.length; i++) {
        const attachmentFile = files[i];
        logUsage('AttachmentCreation', [isInline, shouldShare], {
            cosmosOnlyData: JSON.stringify({
                fileType: attachmentFile.fileType,
                size: attachmentFile.size,
                ext: getExtensionWithoutDotFromFileNameForLogging(attachmentFile.name) || '',
            }),
        });
    }
}

function logAttachmentDeletion(attachmentViewState: AttachmentState) {
    const eventName = 'AttachmentDeletion';
    const attachmentType = attachmentViewState.attachmentType;
    switch (attachmentType) {
        case AttachmentStateType.InlineAttachment:
            logUsage(eventName, [true, false, 0]);
            break;
        case AttachmentStateType.Attachment:
            const attachmentModel = getAttachment(attachmentViewState.attachmentId);
            logUsage(eventName, [false, (<AttachmentFullViewState>attachmentViewState).isCloudy], {
                cosmosOnlyData: JSON.stringify({ size: attachmentModel.model.Size }),
            });
            break;
        default:
            assertNever(attachmentType);
    }
}
